/**
 * Light or dark, for the apps the panel frames.
 *
 * The panel itself is light. A framed app — the console, a scoring desk — gets
 * this in `hello`, and again whenever the person switches it. Saved in the
 * browser so the choice sticks, and shared across tabs.
 */

import type { Theme } from "@fanos/omnium-bridge";

export const THEME_KEY = "omnium.theme";

let memory: Theme = "light";
const listeners = new Set<() => void>();

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function getTheme(): Theme {
  try {
    const saved = storage()?.getItem(THEME_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    // Fall back to memory.
  }
  return memory;
}

export function setTheme(theme: Theme): void {
  memory = theme;
  try {
    storage()?.setItem(THEME_KEY, theme);
  } catch {
    // Kept in memory for this tab only.
  }
  for (const listener of listeners) listener();
}

export function otherTheme(theme: Theme): Theme {
  return theme === "dark" ? "light" : "dark";
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === THEME_KEY) listener();
  };
  if (typeof window !== "undefined") window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    if (typeof window !== "undefined") window.removeEventListener("storage", onStorage);
  };
}
